import { useState } from "react";
import DOMPurify from "dompurify";
import { CaretDown, CaretRight } from "@phosphor-icons/react";

// Markers that signal the start of trailing "chrome" in an email: external-sender CAUTION banners,
// quoted reply history (From:/Sent:), disclaimers/confidentiality notices, and mobile sign-offs.
// We collapse everything from the FIRST such marker so the actual message stands out; the rest is
// one click away (never discarded).
const EMAIL_TRAILER_MARKERS = [
  /caution:\s*this email originated/i,
  /^\s*disclaimer\b/i,
  /the information contained in this (e-?mail|message)\s+is\s+(private|confidential)/i,
  /this (e-?mail|message)[^.]{0,90}(confidential|privileged|intended (only )?for)/i,
  /confidential(ity)? notice/i,
  /^\s*from:\s[\s\S]{0,300}?\b(sent|date):/i,
  /sent from (my )?(outlook|iphone|ipad|mobile|android|samsung|blackberry)/i,
];

// Split sanitized email HTML into a lead (the real message) and a collapsible trailer. Returns null
// when there's nothing worth collapsing (short notes, agent replies, no markers).
function splitEmailTrailer(html) {
  try {
    if (!html || typeof DOMParser === "undefined") return null;
    const doc = new DOMParser().parseFromString(html, "text/html");
    let root = doc.body;
    for (let d = 0; d < 4; d++) {
      const els = Array.from(root.children);
      if (els.length === 1 && /^(div|body|section|table|tbody|tr|td|span)$/i.test(els[0].tagName)) root = els[0];
      else break;
    }
    const blocks = Array.from(root.childNodes).filter(
      (n) => n.nodeType === 1 || (n.nodeType === 3 && n.textContent.trim())
    );
    if (blocks.length < 2) return null;
    let seen = 0, cut = -1;
    for (let i = 0; i < blocks.length; i++) {
      const t = (blocks[i].textContent || "").replace(/\s+/g, " ").trim();
      if (cut === -1 && seen >= 25 && EMAIL_TRAILER_MARKERS.some((re) => re.test(t))) { cut = i; break; }
      seen += t.length;
    }
    if (cut <= 0) return null;
    const lead = blocks.slice(0, cut), trail = blocks.slice(cut);
    const leadText = lead.map((n) => n.textContent || "").join("").trim();
    const trailText = trail.map((n) => n.textContent || "").join("").trim();
    if (leadText.length < 3 || trailText.length < 40) return null; // not worth collapsing
    const toHtml = (nodes) => nodes.map((n) => (n.nodeType === 1 ? n.outerHTML : (n.textContent || ""))).join("");
    return { lead: toHtml(lead), trail: toHtml(trail) };
  } catch (e) {
    return null;
  }
}

// Renders email content: sanitized HTML (so inline images / formatting show in place) when the
// text looks like HTML, otherwise plain text with preserved line breaks. Inline images are
// click-to-zoom into a full-size lightbox, matching the Attachments behaviour. Long signatures,
// disclaimers and quoted history collapse into a "show trimmed content" toggle.
export function RichText({ text, className = "" }) {
  const [zoomSrc, setZoomSrc] = useState(null);
  const [showTrailer, setShowTrailer] = useState(false);
  const str = text || "";
  const looksHtml = /<\/?[a-z][\s\S]*>/i.test(str) || str.includes("data:image");
  if (!looksHtml) {
    return <p className={`whitespace-pre-wrap ${className}`}>{str}</p>;
  }
  const clean = DOMPurify.sanitize(str, {
    ADD_TAGS: ["img"],
    ADD_ATTR: ["src", "alt", "width", "height", "style"],
    FORBID_TAGS: ["script", "style", "iframe", "object", "embed", "link", "meta"],
    FORBID_ATTR: ["onerror", "onload", "onclick"],
  });
  const split = splitEmailTrailer(clean);
  const onClick = (e) => {
    if (e.target && e.target.tagName === "IMG" && e.target.src) {
      setZoomSrc(e.target.src);
    }
  };
  const bodyClass = `email-html max-w-none [&_img]:max-w-full [&_img]:h-auto [&_img]:rounded-sm [&_img]:my-2 [&_img]:cursor-zoom-in [&_a]:text-primary [&_a]:underline [&_table]:max-w-full ${className}`;
  return (
    <>
      {split ? (
        <div onClick={onClick} data-testid="email-html-body">
          <div className={bodyClass} dangerouslySetInnerHTML={{ __html: split.lead }} />
          <button
            type="button"
            data-testid="email-trailer-toggle"
            onClick={(e) => { e.stopPropagation(); setShowTrailer((v) => !v); }}
            className="mt-2 inline-flex items-center gap-1 text-[11px] font-mono uppercase tracking-wider text-muted-foreground hover:text-foreground border border-border rounded-sm px-2 py-1 transition-colors"
          >
            {showTrailer ? <CaretDown size={12} /> : <CaretRight size={12} />}
            {showTrailer ? "Hide signature & quoted history" : "Show signature, disclaimer & quoted history"}
          </button>
          {showTrailer && (
            <div data-testid="email-trailer-content" className={`${bodyClass} mt-2 pt-2 border-t border-dashed border-border/60 opacity-80`} dangerouslySetInnerHTML={{ __html: split.trail }} />
          )}
        </div>
      ) : (
        <div
          onClick={onClick}
          data-testid="email-html-body"
          className={bodyClass}
          dangerouslySetInnerHTML={{ __html: clean }}
        />
      )}
      {zoomSrc && (
        <div
          data-testid="inline-image-lightbox"
          onClick={() => setZoomSrc(null)}
          className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center p-6 cursor-zoom-out"
        >
          <img src={zoomSrc} alt="preview" className="max-w-full max-h-full object-contain rounded-sm" />
        </div>
      )}
    </>
  );
}
